// Định nghĩa interface cho dữ liệu đăng nhập
import { getApiUrl } from '../utils/config';
import axios from 'axios';
import CONFIG from '../utils/config';

export interface AuthResponse {
  token: string;
  type: string;
  id: number;
  username: string;
  clubMemberId?: number;
}

// Interface User
export interface User {
  userId?: number;
  id?: string;
  memberId?: string;
  stravaId?: string;
  clubMemberId?: string;
  fullName?: string;
  createdAt?: string;
  points?: number;
  roles?: string[];
  username?: string;
}

// Interface cho đổi mật khẩu
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// Cập nhật interface AuthData
export interface AuthData {
  token: string;
  type: string;
  id: number;
  username: string;
  clubMemberId?: number;
}

// Interface cho refresh token request
export interface RefreshTokenRequest {
  refreshToken: string;
}

// Interface cho refresh token response
export interface RefreshTokenResponse {
  result: string;
  message: string;
  data?: {
    token: string;
    tokenType: string;
    expiresIn: number;
    refreshToken: string;
    refreshTokenExpiresIn: number;
    user: {
      id: number;
      username: string;
      email: string;
      firstName: string;
      lastName: string;
      profileImageUrl: string | null;
      gender: string | null;
      role: string;
      active: boolean;
      stravaAthleteId: number | null;
      hasStravaToken: boolean;
      createdAt: any;
      updatedAt: any;
    };
  };
}

// Tên các key lưu trong localStorage
const AUTH_TOKEN_KEY = 'jwt_token';
const AUTH_TYPE_KEY = 'auth_type';
const AUTH_USER_ID_KEY = 'auth_user_id';
const AUTH_USERNAME_KEY = 'auth_username';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Cập nhật hàm saveAuthData
export const saveAuthData = (data: {
  token: string;
  type: string;
  id: number;
  username: string;
  clubMemberId?: number;
}) => {
  localStorage.setItem(AUTH_TOKEN_KEY, data.token);
  localStorage.setItem(AUTH_TYPE_KEY, data.type);
  localStorage.setItem(AUTH_USER_ID_KEY, data.id.toString());
  localStorage.setItem(AUTH_USERNAME_KEY, data.username);
  
  // Lưu clubMemberId nếu có
  if (data.clubMemberId) {
    localStorage.setItem('club_member_id', data.clubMemberId.toString());
  }
};

// Cập nhật hàm getAuthData
export const getAuthData = (): AuthData | null => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const type = localStorage.getItem(AUTH_TYPE_KEY);
  const userId = localStorage.getItem(AUTH_USER_ID_KEY);
  const username = localStorage.getItem(AUTH_USERNAME_KEY);
  const clubMemberId = localStorage.getItem('club_member_id');
  
  if (!token || !type || !userId || !username) {
    return null;
  }
  
  return {
    token,
    type,
    id: parseInt(userId, 10),
    username,
    clubMemberId: clubMemberId ? parseInt(clubMemberId, 10) : undefined
  };
};

// Lấy token đã lưu
export const getAuthToken = (): string | null => {
  // Kiểm tra cả hai key lưu trữ token
  const jwtToken = localStorage.getItem(AUTH_TOKEN_KEY);
  const token = localStorage.getItem('token');
  
  // Ưu tiên token từ AUTH_TOKEN_KEY, nếu không có thì dùng key 'token'
  return jwtToken || token;
};

// Lấy loại token (Bearer)
export const getAuthType = (): string | null => {
  // Kiểm tra cả localStorage.getItem(AUTH_TYPE_KEY) và localStorage.getItem('tokenType')
  const authType = localStorage.getItem(AUTH_TYPE_KEY);
  const tokenType = localStorage.getItem('tokenType');
  
  // Nếu không tìm thấy type, mặc định là "Bearer"
  return authType || tokenType || "Bearer";
};

// Lấy ID của người dùng đã đăng nhập
export const getUserId = (): number | null => {
  // Từ hàm getUserInfo()
  const user = getUserInfo();
  if (user && user.userId) {
    return user.userId;
  }
  
  // Kiểm tra trong localStorage với key cũ
  const storedUserId = localStorage.getItem(AUTH_USER_ID_KEY);
  if (storedUserId) {
    return parseInt(storedUserId, 10);
  }
  
  // Kiểm tra trong localStorage với định dạng mới
  try {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const userData = JSON.parse(userJson);
      if (userData.id) {
        return userData.id;
      }
    }
  } catch (error) {
    console.error('Lỗi khi đọc userId từ localStorage:', error);
  }
  
  return null;
};

// Lấy tên đăng nhập
export const getUsername = (): string | null => {
  const username = localStorage.getItem(AUTH_USERNAME_KEY);
  console.log('getUsername called, result:', username);
  return username;
};

// Kiểm tra xem người dùng đã đăng nhập chưa
export const isAuthenticated = (): boolean => {
  const hasToken = !!getAuthToken();
  console.log('isAuthenticated called, result:', hasToken);
  return hasToken;
};

// Xóa thông tin đăng nhập (đăng xuất)
export const clearAuthData = (): void => {
  // Xóa theo định dạng mới
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_TYPE_KEY);
  localStorage.removeItem(AUTH_USER_ID_KEY);
  localStorage.removeItem(AUTH_USERNAME_KEY);
  
  // Xóa theo định dạng cũ
  localStorage.removeItem('token');
  localStorage.removeItem('tokenType');
  localStorage.removeItem('expiresIn');
  localStorage.removeItem('user');
  
  // Xóa refresh token
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('refreshTokenExpiresIn');
  
  // Xóa các thông tin khác liên quan
  localStorage.removeItem('club_member_id');
  localStorage.removeItem('auth_header');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('access_token');
  
  // Thêm log để xác nhận đã xóa
  console.log('Đã xóa tất cả thông tin đăng nhập khỏi localStorage');
};

// Lấy chuỗi Authorization header
export const getAuthHeader = (): string | null => {
  const token = getAuthToken();
  const type = getAuthType();
  
  console.log('getAuthHeader called:');
  console.log('- Token:', token ? `${token.substring(0, 10)}...` : 'null');
  console.log('- Type:', type);
  
  if (!token) {
    console.log('Không tìm thấy token xác thực trong localStorage');
    return null;
  }
  
  return `${type} ${token}`;
};

// Hàm kiểm tra tình trạng đăng nhập (debug)
export const checkAuthStatus = (): void => {

};

// Lấy roles của người dùng
export const getUserRoles = (): string[] => {
  const token = getAuthToken();
  if (!token) {
    return [];
  }
  
  try {
    // Kiểm tra trong localStorage.user trước
    const userJson = localStorage.getItem('user');
    if (userJson) {
      try {
        const userData = JSON.parse(userJson);
        console.log('User data from localStorage:', userData);
        // Nếu có trường role, chuyển về mảng ROLE_XXX
        if (userData.role) {
          return [`ROLE_${userData.role.toUpperCase()}`];
        }
      } catch (e) {
        console.error('Lỗi khi parse user từ localStorage', e);
      }
    }
    
    // Debug log
    console.log('Token để phân tích roles:', token);
    
    // Lấy phần payload của JWT token (phần giữa 2 dấu chấm)
    const parts = token.split('.');
    console.log('Token parts:', parts.length);
    
    if (parts.length !== 3) {
      console.error('Token không hợp lệ, không có 3 phần.');
      return [];
    }
    
    // Decode payload
    const payload = JSON.parse(atob(parts[1]));
    console.log('Token payload:', payload);

    // Kiểm tra các trường khác nhau
    let roles: string[] = [];
    
    // Kiểm tra trường roles
    if (Array.isArray(payload.roles)) {
      roles = payload.roles;
    } 
    // Kiểm tra trường authorities
    else if (Array.isArray(payload.authorities)) {
      roles = payload.authorities;
    }
    // Kiểm tra trường role
    else if (payload.role) {
      roles = [`ROLE_${payload.role.toUpperCase()}`];
    }
    
    console.log('Extracted roles:', roles);
    return roles;
  } catch (error) {
    console.error('Lỗi khi lấy roles từ token:', error);
    return [];
  }
};

// Hàm lấy role chính (để tương thích ngược với code cũ)
export const getUserRole = (): string | null => {
  const roles = getUserRoles();
  // Trả về role cao nhất (ưu tiên admin)
  if (roles.includes('ROLE_ADMIN')) return 'ROLE_ADMIN';
  if (roles.includes('ROLE_USER')) return 'ROLE_USER';
  return roles.length > 0 ? roles[0] : null;
};

// Lấy thông tin người dùng từ token
export const getUserInfo = (): User | null => {
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  
  try {
    // Thử lấy thông tin từ token
    try {
      // Lấy phần payload của JWT token (phần giữa 2 dấu chấm)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const username = getUsername() || payload.sub || '';
      
      return {
        userId: payload.id || parseInt(localStorage.getItem(AUTH_USER_ID_KEY) || '0', 10),
        id: payload.id?.toString() || '',
        username: username,
        stravaId: payload.stravaId || null,
        roles: payload.roles || [],
        createdAt: payload.createdAt || undefined,
        fullName: payload.fullName || ''
      };
    } catch (tokenError) {
      console.error('Lỗi khi đọc token, thử lấy thông tin từ localStorage:', tokenError);
      
      // Lấy thông tin từ localStorage nếu có
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try {
          const userData = JSON.parse(userJson);
          return {
            userId: userData.id || 0,
            id: userData.id?.toString() || '',
            username: userData.username || '',
            stravaId: userData.stravaAthleteId?.toString() || null,
            roles: userData.role ? [`ROLE_${userData.role.toUpperCase()}`] : [],
            createdAt: userData.createdAt || undefined,
            fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
          };
        } catch (localStorageError) {
          console.error('Lỗi khi đọc thông tin user từ localStorage:', localStorageError);
          return null;
        }
      }
      return null;
    }
  } catch (error) {
    console.error('Lỗi khi lấy thông tin người dùng:', error);
    return null;
  }
};

// Hiển thị log chi tiết thông tin token và roles
export const logRoles = (): void => {
  const token = getAuthToken();
  if (!token) {
    console.log('Không có token');
    return;
  }
  
  try {
    // Lấy phần payload của JWT token
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Log thông tin chi tiết
    console.log('------- THÔNG TIN TOKEN -------');
    console.log('Token:', token.substring(0, 20) + '...');
    console.log('Payload:', payload);
    console.log('User ID:', payload.id || getUserId());
    console.log('Username:', payload.sub || getUsername());
    console.log('Roles:', payload.roles || []);
    console.log('------------------------------');
  } catch (error) {
    console.error('Lỗi khi xử lý token:', error);
  }
};

// Kiểm tra xem người dùng có phải là admin không
export const isAdmin = (): boolean => {
  console.log('isAdmin check start');
  
  // Kiểm tra từ token
  const roles = getUserRoles();
  console.log('Roles from getUserRoles:', roles);
  
  // Kiểm tra nhiều cách biểu diễn quyền ADMIN khác nhau
  const adminChecks = [
    'ROLE_ADMIN',
    'ADMIN',
    'admin',
    'Admin'
  ];
  
  for (const role of roles) {
    for (const adminRole of adminChecks) {
      if (role === adminRole || role.includes(adminRole)) {
        console.log(`Admin match found: "${role}" includes "${adminRole}"`);
        return true;
      }
    }
  }
  
  // Kiểm tra từ thông tin lưu trong localStorage
  try {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const userData = JSON.parse(userJson);
      console.log('User data from localStorage for admin check:', userData);
      
      // Kiểm tra nếu có role hoặc roles
      if (userData.role) {
        const userRole = userData.role.toUpperCase();
        if (userRole === 'ADMIN' || userRole.includes('ADMIN')) {
          console.log('Admin found in localStorage.user.role:', userRole);
          return true;
        }
      }
      
      // Kiểm tra trường roles nếu là mảng
      if (Array.isArray(userData.roles)) {
        for (const role of userData.roles) {
          if (typeof role === 'string' && 
              (role.toUpperCase() === 'ADMIN' || 
               role.includes('ADMIN') || 
               role === 'ROLE_ADMIN')) {
            console.log('Admin found in localStorage.user.roles array:', role);
            return true;
          }
        }
      }
      
      // Kiểm tra trường authorities
      if (Array.isArray(userData.authorities)) {
        for (const auth of userData.authorities) {
          if (typeof auth === 'string' && 
              (auth.toUpperCase() === 'ADMIN' || 
               auth.includes('ADMIN') || 
               auth === 'ROLE_ADMIN')) {
            console.log('Admin found in localStorage.user.authorities:', auth);
            return true;
          }
        }
      }
    }
  } catch (error) {
    console.error('Lỗi khi kiểm tra quyền admin từ localStorage:', error);
  }
  
  // Force admin cho debug (uncomment nếu cần)
  // console.log('Force admin mode enabled for debug');
  // return true;
  
  console.log('No admin privileges found');
  return false;
};

// Hàm đổi mật khẩu
export const changePassword = async (data: ChangePasswordRequest): Promise<{success: boolean; message: string}> => {
  try {
    const token = getAuthToken();
    const type = getAuthType();
    
    if (!token) {
      return { success: false, message: 'Chưa đăng nhập' };
    }
    
    const response = await fetch(getApiUrl('/api/auth/change-password'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${type} ${token}`
      },
      body: JSON.stringify(data)
    });
    
    if (response.ok) {
      return { success: true, message: 'Đổi mật khẩu thành công!' };
    } else {
      const errorData = await response.json();
      return { success: false, message: errorData.message || 'Không thể đổi mật khẩu' };
    }
  } catch (error) {
    console.error('Lỗi khi đổi mật khẩu:', error);
    return { success: false, message: 'Lỗi kết nối đến máy chủ' };
  }
};

// Thêm hàm getClubMemberId()
export const getClubMemberId = (): number | null => {
  const userData = getAuthData();
  if (userData && userData.clubMemberId) {
    return userData.clubMemberId;
  }
  return null;
};

// Thêm interface cho request update user
export interface UpdateUserRequest {
  username?: string;
  email?: string;
  fullName?: string;
  password?: string;
}

// Thêm interface cho response update user
export interface UpdateUserResponse {
  result: string;
  message: string;
  data?: any;
}

/**
 * Cập nhật thông tin người dùng
 * @param userId ID người dùng cần cập nhật
 * @param userData Dữ liệu cập nhật (username, email, fullName, password)
 * @returns Kết quả cập nhật
 */
export const updateUserInfo = async (userId: number, userData: UpdateUserRequest): Promise<UpdateUserResponse> => {
  try {
    const token = getAuthToken();
    if (!token) {
      return {
        result: 'ERROR',
        message: 'Bạn cần đăng nhập để thực hiện thao tác này'
      };
    }

    const response = await fetch(getApiUrl(`/api/admin/users/${userId}`), {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      return {
        result: 'ERROR',
        message: data.message || 'Không thể cập nhật thông tin người dùng'
      };
    }

    return {
      result: 'SUCCESS',
      message: 'Cập nhật thông tin thành công',
      data: data.data
    };
  } catch (error: any) {
    console.error('Lỗi khi cập nhật thông tin người dùng:', error);
    return {
      result: 'ERROR',
      message: error.message || 'Đã xảy ra lỗi khi kết nối đến máy chủ'
    };
  }
};

// Thêm hàm debug để kiểm tra xác thực
export const debugAuth = (): void => {
  console.group('===== AUTH DEBUG INFO =====');
  
  // Kiểm tra token
  console.log('JWT Token:', localStorage.getItem(AUTH_TOKEN_KEY));
  console.log('Token:', localStorage.getItem('token'));
  console.log('TokenType:', localStorage.getItem('tokenType'));
  console.log('User:', localStorage.getItem('user'));
  
  // Phân tích token
  const token = getAuthToken();
  if (token) {
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const header = JSON.parse(atob(parts[0]));
        const payload = JSON.parse(atob(parts[1]));
        
        console.log('Token Header:', header);
        console.log('Token Payload:', payload);
        
        // Kiểm tra các trường quan trọng
        console.log('Subject:', payload.sub);
        console.log('ID:', payload.id || payload.userId);
        console.log('Roles:', payload.roles || payload.authorities || payload.role);
        console.log('Expires:', new Date(payload.exp * 1000).toLocaleString());
      } else {
        console.log('Token không đúng định dạng JWT');
      }
    } catch (e) {
      console.error('Lỗi khi phân tích token:', e);
    }
  } else {
    console.log('Không tìm thấy token');
  }
  
  // Kiểm tra thông tin user
  console.log('getUserInfo():', getUserInfo());
  console.log('getUserRoles():', getUserRoles());
  console.log('isAdmin():', isAdmin());
  console.log('isAuthenticated():', isAuthenticated());
  
  console.groupEnd();
};

export const forceAdmin = (): void => {
  // Uncomment dòng này nếu muốn buộc quyền admin
  // return true;
}

// Thêm hàm để lấy refresh token
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

// Thêm hàm để refresh token
export const refreshTokenAndSave = async (): Promise<RefreshTokenResponse> => {
  try {
    const refreshToken = getRefreshToken();
    
    if (!refreshToken) {
      return {
        result: 'ERROR',
        message: 'Không tìm thấy refresh token',
      };
    }
    
    const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.AUTH.REFRESH_TOKEN), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      // Xử lý khi refresh token thất bại
      console.error('Không thể làm mới token:', data.message);
      return {
        result: 'ERROR',
        message: data.message || 'Không thể làm mới token',
      };
    }
    
    // Lưu token mới vào localStorage
    if (data.data?.token) {
      // Lưu token theo cấu trúc cũ
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('tokenType', data.data.tokenType);
      localStorage.setItem('expiresIn', data.data.expiresIn.toString());
      
      // Lưu refresh token mới
      localStorage.setItem(REFRESH_TOKEN_KEY, data.data.refreshToken);
      localStorage.setItem('refreshTokenExpiresIn', data.data.refreshTokenExpiresIn.toString());
      
      localStorage.setItem('user', JSON.stringify(data.data.user));
      
      // Lưu token theo định dạng mới cho authService
      localStorage.setItem(AUTH_TOKEN_KEY, data.data.token);
      localStorage.setItem(AUTH_TYPE_KEY, data.data.tokenType);
      localStorage.setItem(AUTH_USER_ID_KEY, data.data.user.id.toString());
      localStorage.setItem(AUTH_USERNAME_KEY, data.data.user.username);
      
      console.log('Đã làm mới token:', {
        jwt_token: data.data.token.substring(0, 10) + '...',
        refreshToken: data.data.refreshToken.substring(0, 10) + '...',
      });
    }
    
    return data;
  } catch (error) {
    console.error('Lỗi khi làm mới token:', error);
    return {
      result: 'ERROR',
      message: 'Đã xảy ra lỗi khi kết nối đến máy chủ',
    };
  }
}; 